// @flow

import './DesktopHeader.scss';

import React, { useContext, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { CognitoContext } from '../../CognitoUtils';
import { noPurchasePolicy } from '../../constants/commonConstants';
import { allInsuranceTypes } from '../../constants/insuranceTypeConstants';
import { localPhoneNumber } from '../../constants/phoneNumbers';
import {
  capitalise,
  dashboardLinkEnabled,
  isAgent,
  withPrefix
} from '../../helpers';
import { journeyEnabled } from '../../helpers/FeatureHelper';
import { routes } from '../../routes';
import type { InsuranceType } from '../../types/commonTypes';
import UserContext from '../../UserContext';
import { UserProfileContext } from '../../UserProfile';
import { goToUrlForcingLoggedIn } from '../../utils/utils';
import { SubHeadingMenu } from './SubHeadingMenu';
import { SubHeadingMenuItem } from './SubHeadingMenuItem';

interface Props {
  isLoggedIn: boolean;
}

const noPurchaseText = `${noPurchasePolicy}. Please call ${localPhoneNumber} for assistance.`;
const manageMyInsuranceProductURL = routes.viewMultiplePolicies.url;
const className = 'c-SubHeading';
const withClass = withPrefix('c-SubHeading');

const quoteUrls = {
  car: `${routes.getGenericQuotePage.url}car`,
  home: `${routes.getQuotePage.url}aboutYou`,
  travel: `${routes.getGenericQuotePage.url}travel`
};

// eslint-disable-next-line complexity
export const SubHeading = ({ isLoggedIn }: Props) => {
  const [selectedMenu, setSelectedMenu] = useState<?string>(undefined);
  const [lastSelectedMenu, setLastSelectedMenu] = useState<?string>(undefined);
  const { feature } = useContext(CognitoContext);

  const location = useLocation();
  const history = useHistory();
  const { userProfile } = useContext(UserProfileContext);
  const { agentScriptPending } = useContext(UserContext);

  useEffect(() => {
    let isMounted = true;
    if (selectedMenu) {
      setLastSelectedMenu(selectedMenu);
    }
    const listener = () => isMounted && setSelectedMenu(undefined);
    document.addEventListener('click', listener);
    return () => {
      isMounted = false;
      document.removeEventListener('click', listener);
    };
  }, [selectedMenu]);

  const goToManageForcingLoggedIn = (suffix: string) => () => {
    goToUrlForcingLoggedIn(`${manageMyInsuranceProductURL}${suffix}`, history);
  };

  const manageInsuranceSubmenu = () => {
    const insuranceLinks = allInsuranceTypes
      .filter(
        (insuranceType: InsuranceType) =>
          (!isLoggedIn ||
            (userProfile?.purchasedInsuranceTypes || {})[insuranceType]) &&
          journeyEnabled(feature.flags)(insuranceType)
      )
      .map((insuranceType: string, i: number) => (
        <SubHeadingMenuItem
          text={capitalise(insuranceType)}
          key={`insuranceLink_${i}`}
          index={`insuranceLink_${i}`}
          onClick={goToManageForcingLoggedIn(insuranceType)}
        />
      ));
    return insuranceLinks?.length ? (
      insuranceLinks
    ) : (
      <SubHeadingMenuItem text={noPurchaseText} index="noPurchase" />
    );
  };

  const isManageInsuranceUnderline = (menuKey: string) =>
    location.pathname.includes(manageMyInsuranceProductURL) &&
    lastSelectedMenu === menuKey;

  return (
    <div className={className}>
      <div className={withClass('__linksContainer')}>
        <SubHeadingMenu
          index="home"
          link={routes.wallet.url}
          text="Home"
          setSelectedMenu={setSelectedMenu}
          underline={location.pathname === '/wallet'}
          disabled={!dashboardLinkEnabled(agentScriptPending, location)}
        />

        {!isAgent() && (
          <>
            <SubHeadingMenu
              index="newQuote"
              text="New Quote"
              selectedMenu={selectedMenu}
              setSelectedMenu={setSelectedMenu}
              underline={Object.values(quoteUrls).includes(location.pathname)}
            >
              {feature.flags?.motor_enabled && (
                <SubHeadingMenuItem
                  text="Car"
                  link={quoteUrls.car}
                  index="nqCar"
                />
              )}
              {feature.flags?.home_enabled && (
                <SubHeadingMenuItem
                  text="Home"
                  link={quoteUrls.home}
                  index="nqHome"
                />
              )}
              {feature.flags?.travel_enabled && (
                <SubHeadingMenuItem
                  text="Travel"
                  link={quoteUrls.travel}
                  index="nqTravel"
                />
              )}
            </SubHeadingMenu>

            <SubHeadingMenu
              index="manageInsurance"
              text="Manage My Insurance"
              selectedMenu={selectedMenu}
              setSelectedMenu={setSelectedMenu}
              underline={isManageInsuranceUnderline('manageInsurance')}
            >
              {manageInsuranceSubmenu()}
            </SubHeadingMenu>

            <SubHeadingMenu
              index="renew"
              text="Renew"
              onClick={goToManageForcingLoggedIn('home')}
              setSelectedMenu={setSelectedMenu}
              underline={isManageInsuranceUnderline('renew')}
            />
          </>
        )}
      </div>
    </div>
  );
};
